// src/pages/Users.tsx

import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import {
  fetchAllUsers,
  fetchPendingRequests,
  fetchSentRequests,
  fetchMyConnections,
  fetchUserNetworkCounts,
  sendConnectionRequest,
  acceptConnectionRequest,
  rejectConnectionRequest,
  removeConnectionRequest,
  removeAcceptedConnection,
  fetchConnectedUserDetails,
  resetSelectedProfile,
  resetConnectionError,
  resetConnectionMessage,
} from "../redux/slices/connectionSlice";
import Rightchat from "../components/Rightchat";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

type ActiveTab = "all" | "pending" | "sent" | "connections";

const Users: React.FC = () => {
  const dispatch = useDispatch<any>();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const selectedClient = JSON.parse(
    localStorage.getItem("selectedClient") || "{}"
  );
  const loggedClient = JSON.parse(localStorage.getItem("client") || "{}");

  const userId =
    user?.user_id || selectedClient?.user_id || loggedClient?.user_id || null;

  const isLoggedIn = Boolean(userId);

  const {
    users = [],
    myConnections = [],
    pendingRequests = [],
    sentRequests = [],
    counts,
    loading,
    actionLoading,
    profileLoading,
    selectedProfile,
    error,
    message,
  } = useSelector((state: any) => state.connections);

  const [activeTab, setActiveTab] = useState<ActiveTab>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [hiddenUserIds, setHiddenUserIds] = useState<Array<number | string>>([]);
  const [expandedProfileUserId, setExpandedProfileUserId] = useState<
    number | string | null
  >(null);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [selectedRemoveItem, setSelectedRemoveItem] = useState<any>(null);

  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!userId) return;

    dispatch(
      fetchAllUsers({
        user_id: userId,
        search: "",
        limit,
        offset: 0,
      })
    );

    dispatch(fetchPendingRequests(userId));
    dispatch(fetchSentRequests(userId));
    dispatch(fetchMyConnections(userId));
    dispatch(fetchUserNetworkCounts(userId));

    setOffset(0);
  }, [dispatch, userId, limit]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (!userId || activeTab !== "all") return;

      dispatch(
        fetchAllUsers({
          user_id: userId,
          search: searchTerm,
          limit,
          offset: 0,
        })
      );

      setOffset(0);
    }, 450);

    return () => clearTimeout(delaySearch);
  }, [searchTerm, dispatch, userId, limit, activeTab]);

  useEffect(() => {
    if (!message) return;

    const hiddenFetchMessages = [
      "Users not connected yet fetched successfully",
      "Pending requests fetched successfully",
      "Sent requests fetched successfully",
      "My connections fetched successfully",
      "Connections fetched successfully",
      "Users fetched successfully",
      "User network counts fetched successfully",
      "Profile details fetched successfully",
    ];

    const shouldHideToast = hiddenFetchMessages.some((text) =>
      String(message).toLowerCase().includes(text.toLowerCase())
    );

    if (!shouldHideToast) toast.success(message);

    dispatch(resetConnectionMessage());
  }, [message, dispatch]);

  useEffect(() => {
    if (!error) return;

    toast.error(error);
    dispatch(resetConnectionError());
  }, [error, dispatch]);




  const getConnectionProfileUserId = (item: any) => {
    if (!item) return null;

    if (item.connected_user_id) return item.connected_user_id;

    if (item.sender_user_id && item.receiver_user_id && userId) {
      return String(item.sender_user_id) === String(userId)
        ? item.receiver_user_id
        : item.sender_user_id;
    }

    return item.user_id || item.profile_user_id || null;
  };




  const removeDuplicateList = (list: any[]) => {
    const map = new Map<string, any>();

    list.forEach((item: any) => {
      const key =
        activeTab === "pending"
          ? item.connection_id || item.sender_user_id || item.user_id
          : activeTab === "sent"
          ? item.connection_id || item.receiver_user_id || item.user_id
          : activeTab === "connections"
          ? getConnectionProfileUserId(item)
          : item.user_id;

      if (!key) return;

      if (!map.has(String(key))) {
        map.set(String(key), item);
      }
    });

    return Array.from(map.values());
  };

  const currentList = useMemo(() => {
    if (activeTab === "pending") {
      return removeDuplicateList(
        Array.isArray(pendingRequests) ? pendingRequests : []
      );
    }

    if (activeTab === "sent") {
      return removeDuplicateList(Array.isArray(sentRequests) ? sentRequests : []);
    }

    if (activeTab === "connections") {
      return removeDuplicateList(
        Array.isArray(myConnections) ? myConnections : []
      );
    }

    return Array.isArray(users) ? users : [];
  }, [activeTab, users, pendingRequests, sentRequests, myConnections]);

  const visibleList = useMemo(() => {
    if (activeTab !== "all") return currentList;

    return currentList.filter((item: any) => {
      const suggestionUserId = item?.user_id;

      return !hiddenUserIds.some(
        (hiddenId) => String(hiddenId) === String(suggestionUserId)
      );
    });
  }, [activeTab, currentList, hiddenUserIds]);

  const getInitial = (name?: string) => {
    if (!name || String(name).trim() === "") return "U";
    return String(name).trim().charAt(0).toUpperCase();
  };

  const getUploadUrl = (
    pathValue?: string | null,
    fallbackFolder = ""
  ) => {
    if (
      !pathValue ||
      String(pathValue).trim() === ""
    ) {
      return "";
    }

    const imagePath =
      String(pathValue).trim();

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return encodeURI(imagePath);
    }

    if (
      imagePath.startsWith("/uploads/")
    ) {
      return encodeURI(
        `${API_URL}${imagePath}`
      );
    }

    if (
      imagePath.startsWith("uploads/")
    ) {
      return encodeURI(
        `${API_URL}/${imagePath}`
      );
    }

    if (fallbackFolder) {
      const folder =
        fallbackFolder.startsWith("/")
          ? fallbackFolder
          : `/${fallbackFolder}`;

      return encodeURI(
        `${API_URL}${folder}/${imagePath.replace(/^\/+/, "")}`
      );
    }

    if (imagePath.startsWith("/")) {
      return encodeURI(
        `${API_URL}${imagePath}`
      );
    }

    return encodeURI(
      `${API_URL}/${imagePath}`
    );
  };

  const getProfilePhoto = (photo?: string | null) => {
    return getUploadUrl(photo, "/uploads/profile-photos");
  };

  const getClientLogo = (logo?: string | null) => {
    return getUploadUrl(logo);
  };

  const getCommunityText = (item: any) => {
    return (
      item?.client_name ||
      item?.club_name ||
      item?.company_name ||
      item?.business_name ||
      item?.contact_company_details ||
      "-"
    );
  };

  const displayValue = (value?: string | number | null) => {
    if (value === null || value === undefined || String(value).trim() === "") {
      return "Not available";
    }

    return value;
  };

  const formatDate = (dateValue?: string | null) => {
    if (!dateValue) return "Not available";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return String(dateValue);

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };



  const handleRemoveSuggestion = (removeUserId: number | string) => {
    if (!removeUserId) return;

    setHiddenUserIds((prev) => {
      const alreadyHidden = prev.some(
        (hiddenId) => String(hiddenId) === String(removeUserId)
      );

      if (alreadyHidden) return prev;
      return [...prev, removeUserId];
    });
  };

  const refreshNetworkData = () => {
    if (!userId) return;

    dispatch(fetchPendingRequests(userId));
    dispatch(fetchSentRequests(userId));
    dispatch(fetchMyConnections(userId));
    dispatch(fetchUserNetworkCounts(userId));

    dispatch(
      fetchAllUsers({
        user_id: userId,
        search: searchTerm,
        limit,
        offset: 0,
      })
    );
  };

  const handleConnect = async (receiverUserId: number | string) => {
    if (!userId || !receiverUserId) return;

    const result = await dispatch(
      sendConnectionRequest({
        sender_user_id: userId,
        receiver_user_id: receiverUserId,
      })
    );

    if (sendConnectionRequest.rejected.match(result)) {
      return;
    }

    refreshNetworkData();
  };

  const handleAccept = async (connectionId: number | string) => {
    if (!connectionId) return;

    const result = await dispatch(
      acceptConnectionRequest({
        connection_id: connectionId,
      })
    );

    if (acceptConnectionRequest.rejected.match(result)) {
      return;
    }

    refreshNetworkData();
  };

  const handleReject = async (connectionId: number | string) => {
    if (!connectionId) return;

    const result = await dispatch(
      rejectConnectionRequest({
        connection_id: connectionId,
      })
    );

    if (rejectConnectionRequest.rejected.match(result)) {
      return;
    }

    refreshNetworkData();
  };

  const handleCancelSentRequest = async (item: any) => {
    if (!userId || !item) return;

    const result = await dispatch(
      removeConnectionRequest({
        connection_id: item.connection_id,
        sender_user_id: item.sender_user_id || userId,
        receiver_user_id: item.receiver_user_id || item.user_id,
      })
    );

    if (removeConnectionRequest.rejected.match(result)) {
      return;
    }

    refreshNetworkData();
  };

  const handleRemoveAcceptedConnection = async (item: any) => {
    if (!userId || !item) return;

    const result = await dispatch(
      removeAcceptedConnection({
        connection_id: item.connection_id,
        user_id: userId,
        connected_user_id:
          item.connected_user_id ||
          item.user_id ||
          getConnectionProfileUserId(item),
      })
    );

    if (removeAcceptedConnection.rejected.match(result)) {
      return;
    }

    setExpandedProfileUserId(null);
    dispatch(resetSelectedProfile());
    refreshNetworkData();
  };

  const openRemoveConnectionModal = (item: any) => {
    setSelectedRemoveItem(item);
    setRemoveModalOpen(true);
  };

  const closeRemoveConnectionModal = () => {
    if (actionLoading) return;
    setSelectedRemoveItem(null);
    setRemoveModalOpen(false);
  };

  const confirmRemoveConnection = async () => {
    if (!selectedRemoveItem) return;

    await handleRemoveAcceptedConnection(selectedRemoveItem);
    setSelectedRemoveItem(null);
    setRemoveModalOpen(false);
  };

  const handleViewDetails = async (item: any) => {
    const profileUserId = getConnectionProfileUserId(item);

    if (!userId || !profileUserId) return;

    if (String(expandedProfileUserId) === String(profileUserId)) {
      setExpandedProfileUserId(null);
      dispatch(resetSelectedProfile());
      return;
    }

    setExpandedProfileUserId(profileUserId);
    dispatch(resetSelectedProfile());

    await dispatch(
      fetchConnectedUserDetails({
        viewer_user_id: userId,
        profile_user_id: profileUserId,
      })
    );
  };

  const closeProfileSheet = () => {
    setExpandedProfileUserId(null);
    dispatch(resetSelectedProfile());
  };

  const handleLoadMore = () => {
    if (!userId || activeTab !== "all") return;

    const nextOffset = offset + limit;
    setOffset(nextOffset);

    dispatch(
      fetchAllUsers({
        user_id: userId,
        search: searchTerm,
        limit,
        offset: nextOffset,
      })
    );
  };

  const getCardDesignation = (item: any) => {
    return (
      item?.designation_name ||
      item?.job_title ||
      item?.profession ||
      item?.contact_profession ||
      "Member"
    );
  };

  const getPageTitle = () => {
    if (activeTab === "pending") return "Received requests";
    if (activeTab === "sent") return "Invitations sent";
    if (activeTab === "connections") return "My connections";
    return "People you may know";
  };

  const getPageDescription = () => {
    if (activeTab === "all") {
      return "Suggested members based on your community network.";
    }

    if (activeTab === "pending") {
      return "Accept or ignore requests from members.";
    }

    if (activeTab === "sent") {
      return "Requests you have sent and are waiting for approval.";
    }

    return "View your accepted community connections.";
  };

  const UserAvatar = ({
    item,
    size = "md",
  }: {
    item: any;
    size?: "sm" | "md" | "lg";
  }) => {
    const photoUrl = getProfilePhoto(
      item?.display_photo ||
        item?.profile_photo ||
        item?.contact_photo ||
        item?.photo
    );

    return (
      <div className={`bs-avatar bs-avatar-${size}`}>
        {photoUrl && (
          <img
            src={photoUrl}
            alt={item?.user_fullname || "User"}
            onError={(e: any) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement
                ?.querySelector(".bs-avatar-fallback")
                ?.classList.add("show");
            }}
          />
        )}

        <span className={`bs-avatar-fallback ${!photoUrl ? "show" : ""}`}>
          {getInitial(item?.user_fullname || item?.contact_name)}
        </span>
      </div>
    );
  };

  const ClientLogo = ({ item }: { item: any }) => {
    const logoUrl = getClientLogo(item?.logo_image);

    if (!logoUrl) {
      return (
        <span className="bs-client-logo-fallback">
          <i className="feather-briefcase" />
        </span>
      );
    }

    return (
      <span className="bs-client-logo">
        <img
          src={logoUrl}
          alt={item?.client_name || "Client"}
          onError={(e: any) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement
              ?.querySelector(".bs-client-logo-fallback")
              ?.classList.add("show");
          }}
        />

        <span className="bs-client-logo-fallback">
          <i className="feather-briefcase" />
        </span>
      </span>
    );
  };

  const renderActionButton = (item: any) => {
    if (activeTab === "pending") {
      return (
        <div className="bs-card-actions two">
          <button
            className="bs-btn bs-btn-primary"
            disabled={actionLoading}
            onClick={() => handleAccept(item.connection_id)}
          >
            Accept
          </button>

          <button
            className="bs-btn bs-btn-light"
            disabled={actionLoading}
            onClick={() => handleReject(item.connection_id)}
          >
            Ignore
          </button>
        </div>
      );
    }

    if (activeTab === "sent") {
      return (
        <div className="bs-card-actions two">
          <button className="bs-btn bs-btn-sent" disabled>
            <i className="feather-clock" />
            Sent
          </button>

          <button
            className="bs-btn bs-btn-light"
            disabled={actionLoading}
            onClick={() => handleCancelSentRequest(item)}
          >
            Cancel
          </button>
        </div>
      );
    }

    if (activeTab === "connections") {
      return (
        <div className="bs-card-actions three">
          <button className="bs-btn bs-btn-connected" disabled>
            <i className="feather-check" />
            Connected
          </button>

          <button
            className="bs-btn bs-btn-outline"
            disabled={
              profileLoading &&
              String(expandedProfileUserId) ===
                String(getConnectionProfileUserId(item))
            }
            onClick={() => handleViewDetails(item)}
          >
            <i
              className={
                String(expandedProfileUserId) ===
                String(getConnectionProfileUserId(item))
                  ? "feather-eye-off"
                  : "feather-eye"
              }
            />

            {String(expandedProfileUserId) ===
            String(getConnectionProfileUserId(item))
              ? "Hide"
              : "Details"}
          </button>

          <button
            className="bs-btn bs-btn-danger"
            disabled={actionLoading}
            onClick={() => openRemoveConnectionModal(item)}
          >
            Remove
          </button>
        </div>
      );
    }

    return (
      <button
        className={`bs-connect-btn ${
          item.connection_status === "pending" ? "sent" : ""
        }`}
        disabled={actionLoading || item.connection_status === "pending"}
        onClick={() => handleConnect(item.user_id)}
      >
        {item.connection_status === "pending" ? (
          <>
            <i className="feather-clock" />
            Sent
          </>
        ) : (
          <>
            <i className="feather-user-plus" />
            {item.button_text || "Connect"}
          </>
        )}
      </button>
    );
  };

  const renderMutualAvatars = (item: any) => {
    if (!Array.isArray(item?.mutual_profiles) || item.mutual_profiles.length === 0) {
      return null;
    }

    return (
      <div className="bs-mutual-box">
        <div className="bs-mutual-stack">
          {item.mutual_profiles.slice(0, 3).map((mutual: any) => {
            const mutualPhoto = getProfilePhoto(mutual.profile_photo);

            return (
              <div
                className="bs-mutual-avatar"
                key={mutual.user_id}
                title={mutual.user_fullname || ""}
              >
                {mutualPhoto ? (
                  <img
                    src={mutualPhoto}
                    alt={mutual.user_fullname || "Mutual"}
                    onError={(e: any) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span>{getInitial(mutual.user_fullname)}</span>
                )}
              </div>
            );
          })}
        </div>

        <p>
          Mutual with{" "}
          {item.mutual_profiles
            .slice(0, 2)
            .map((m: any) => m.user_fullname)
            .filter(Boolean)
            .join(", ")}
        </p>
      </div>
    );
  };

  const renderFullProfileView = () => {
    const profileListItem = currentList.find(
      (item: any) =>
        String(getConnectionProfileUserId(item)) === String(expandedProfileUserId)
    );

    const profile = {
      ...(profileListItem || {}),
      ...(selectedProfile || {}),
    };

    const name = profile.user_fullname || profile.contact_name || "Profile Details";

    const profession =
      profile.job_title ||
      profile.contact_profession ||
      profile.profession ||
      profile.designation_name ||
      "Member";

    const company =
      profile.client_name ||
      profile.company_name ||
      profile.contact_company_details ||
      profile.business_name ||
      profile.club_name;

    const headline = [profession, company].filter(Boolean).join(" at ");

    const location = [
      profile.job_location,
      profile.address,
      profile.taluk_name,
      profile.district_name,
      profile.state_name,
    ]
      .filter((value) => value && String(value).trim() !== "")
      .slice(0, 3)
      .join(", ");

    const aboutText =
      profile.job_description ||
      profile.business_description ||
      profile.about ||
      "Profile details will appear here when the member updates their information.";

    const goBackToConnections = () => {
      setExpandedProfileUserId(null);
      dispatch(resetSelectedProfile());
    };

    if (profileLoading) {
      return (
        <div className="bs-profile-page-card bs-profile-loading-card">
          <div className="spinner-border text-primary" />
          <p>Loading profile details...</p>
        </div>
      );
    }

    if (!selectedProfile) {
      return (
        <div className="bs-profile-page-card bs-profile-empty-state">
          <button className="bs-back-btn" type="button" onClick={goBackToConnections}>
            <i className="feather-arrow-left" />
            Back to connections
          </button>

          <div className="bs-empty-icon">
            <i className="feather-lock" />
          </div>

          <h4>Profile details not available</h4>
          <p>You can view full details only for accepted connections.</p>
        </div>
      );
    }

    return (
      <div className="bs-profile-page-wrap">
        <button className="bs-back-btn" type="button" onClick={goBackToConnections}>
          <i className="feather-arrow-left" />
          Back to connections
        </button>

        <div className="bs-profile-layout">
          <section className="bs-profile-main-column">
            <article className="bs-profile-hero-card">
              <div
                className="bs-profile-cover-image"
                style={
                  profile.banner_image
                    ? {
                        backgroundImage: `url(${getUploadUrl(profile.banner_image)})`,
                      }
                    : undefined
                }
              >
                <span>{profile.client_name}</span>
              </div>

              <div className="bs-profile-hero-body">
                <div className="bs-profile-avatar-large">
                  <UserAvatar item={profile} size="lg" />
                </div>

                <div className="bs-profile-title-row bs-profile-title-clean">
                  <div className="bs-profile-title-left">
                    <div className="bs-profile-name-line">
                      <h2>{name}</h2>

                      <span className="bs-status-pill success compact">
                        <i className="feather-check" />
                        Connected
                      </span>
                    </div>

                    <p>{headline || "Bharat Sangam member"}</p>

                    {location && (
                      <span className="bs-profile-location">{location}</span>
                    )}

                    <div className="bs-profile-status-row">
                      <span className="bs-status-pill neutral">
                        {profession || "Member"}
                      </span>

                      <span className="bs-status-pill neutral soft">
                        {company || getCommunityText(profile)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            <article className="bs-profile-section-card">
              <h3>Highlights</h3>

              <div className="bs-highlight-grid">
                <div className="bs-highlight-item">
                  <UserAvatar item={profile} size="sm" />

                  <div>
                    <strong>{name} is your connection</strong>
                    <p>You and {name} are connected on Bharat Sangam.</p>
                  </div>
                </div>

                <div className="bs-highlight-item">
                  <i className="feather-users" />

                  <div>
                    <strong>{profile.mutual_connections || 0} mutual connections</strong>
                    <p>Community relationship based on your accepted network.</p>
                  </div>
                </div>
              </div>
            </article>

            <article className="bs-profile-section-card">
              <h3>About</h3>
              <p className="bs-about-text">{aboutText}</p>
            </article>

            {(profile.work_type === "job" ||
              profile.job_title ||
              profile.company_name ||
              profile.job_department) && (
              <article className="bs-profile-section-card">
                <h3>Experience</h3>

                <div className="bs-experience-row">
                  <div className="bs-experience-icon">
                    <i className="feather-briefcase" />
                  </div>

                  <div>
                    <h4>{displayValue(profile.job_title || profession)}</h4>
                    <p>{displayValue(profile.company_name || company)}</p>

                    <span>
                      {displayValue(profile.job_department)}
                      {profile.job_experience ? ` • ${profile.job_experience}` : ""}
                    </span>

                    {profile.job_location && <small>{profile.job_location}</small>}
                  </div>
                </div>
              </article>
            )}

            {(profile.work_type === "business" || profile.business_name) && (
              <article className="bs-profile-section-card">
                <h3>Business</h3>

                <div className="bs-experience-row">
                  <div className="bs-experience-icon">
                    <i className="feather-home" />
                  </div>

                  <div>
                    <h4>{displayValue(profile.business_name)}</h4>
                    <p>{displayValue(profile.business_category)}</p>
                    <span>{displayValue(profile.business_address)}</span>

                    {profile.business_website && (
                      <small>{profile.business_website}</small>
                    )}
                  </div>
                </div>
              </article>
            )}
          </section>

          <aside className="bs-profile-side-column">
            <article className="bs-profile-side-card">
              <h3>Contact information</h3>

              <div className="bs-side-info-list">
                <div>
                  <span>Mobile</span>
                  <strong>{displayValue(profile.user_mobile || profile.contact_mobile)}</strong>
                </div>

                <div>
                  <span>Email</span>
                  <strong>{displayValue(profile.user_email || profile.contact_email)}</strong>
                </div>

                <div>
                  <span>Date of Birth</span>
                  <strong>{formatDate(profile.dob || profile.contact_dob)}</strong>
                </div>

                <div>
                  <span>Gender</span>
                  <strong>{displayValue(profile.gender)}</strong>
                </div>
              </div>
            </article>

            <article className="bs-profile-side-card bs-community-card">
              <h3>Community</h3>

              <div className="bs-community-profile-box">
                <ClientLogo item={profile} />

                <div>
                  <strong>{displayValue(getCommunityText(profile))}</strong>
                  <span>{displayValue(profession)}</span>
                </div>
              </div>

              <div className="bs-side-info-list">
                <div>
                  <span>Designation</span>
                  <strong>{displayValue(profession)}</strong>
                </div>

                <div>
                  <span>Work Type</span>
                  <strong>{displayValue(profile.work_type || "Member")}</strong>
                </div>
              </div>
            </article>
          </aside>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat/>

      <div className="main-content right-chat-active bs-users-main">
        <div className="middle-sidebar-bottom bs-middle-bottom">
          <div className="middle-sidebar-left bs-users-content">
            <div className="bs-network-layout">
              <main className="bs-network-main">
                {!isLoggedIn ? (
                  <section className="bs-guest-network-card">
                    <div className="bs-guest-network-icon">
                      <i className="feather-users" />
                    </div>

                    <h2>Community Network</h2>

                    <p className="bs-guest-network-lead">
                      Discover and connect with Bharat Sangam members.
                    </p>

                    <p className="bs-guest-network-note">
                      Sign in to manage connection requests,
                      send invitations and view your network.
                    </p>
                  </section>
                ) : (
                  <>
                {!(activeTab === "connections" && expandedProfileUserId) && (
                  <section className="bs-overview-top-card">
                    <div className="bs-overview-title-block">
                      <h2>Network overview</h2>
                      <p>Manage your invites, connections and community network.</p>
                    </div>

                    <div className="bs-overview-stats">
                      <button
                        type="button"
                        className={`bs-overview-stat ${
                          activeTab === "sent" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("sent")}
                      >
                        <strong>{counts?.sent_requests || sentRequests?.length || 0}</strong>
                        <span>Invites sent</span>
                      </button>

                      <button
                        type="button"
                        className={`bs-overview-stat ${
                          activeTab === "connections" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("connections")}
                      >
                        <strong>{counts?.connections || myConnections?.length || 0}</strong>
                        <span>Connections</span>
                      </button>
                    </div>
                  </section>
                )}

                {activeTab === "connections" && expandedProfileUserId ? (
                  renderFullProfileView()
                ) : (
                  <>
                    <div className="bs-pending-strip">
                      <strong>
                        {pendingRequests?.length > 0
                          ? `${pendingRequests.length} received request${
                              pendingRequests.length > 1 ? "s" : ""
                            }`
                          : "No received requests"}
                      </strong>

                      <button type="button" onClick={() => setActiveTab("pending")}>
                        Manage
                      </button>
                    </div>

                    <div className="bs-section-head">
                      <div>
                        <h3>{getPageTitle()}</h3>
                        <p>{getPageDescription()}</p>
                      </div>

                      <div className="bs-section-actions">
                        {activeTab !== "all" && (
                          <button
                            type="button"
                            className="bs-small-action"
                            onClick={() => setActiveTab("all")}
                          >
                            All Users
                          </button>
                        )}

                        {activeTab !== "pending" && (
                          <button
                            type="button"
                            className="bs-small-action"
                            onClick={() => setActiveTab("pending")}
                          >
                            Requests {pendingRequests?.length || 0}
                          </button>
                        )}

                        {activeTab !== "sent" && (
                          <button
                            type="button"
                            className="bs-small-action"
                            onClick={() => setActiveTab("sent")}
                          >
                            Sent {counts?.sent_requests || sentRequests?.length || 0}
                          </button>
                        )}

                        {activeTab !== "connections" && (
                          <button
                            type="button"
                            className="bs-small-action"
                            onClick={() => setActiveTab("connections")}
                          >
                            Connections {counts?.connections || myConnections?.length || 0}
                          </button>
                        )}

                        <div className="bs-search-box">
                          <i className="feather-search" />

                          <input
                            type="text"
                            placeholder={
                              activeTab === "all"
                                ? "Search by name, client, designation..."
                                : "Search available only in All Users"
                            }
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={activeTab !== "all"}
                          />
                        </div>
                      </div>
                    </div>

                    {loading ? (
                      <div className="bs-loader-card">
                        <div className="spinner-border text-primary" />
                        <p>Loading users...</p>
                      </div>
                    ) : visibleList.length > 0 ? (
                      <>
                        <div
                          className={`bs-people-grid ${
                            activeTab !== "all" ? "bs-people-list-mode" : ""
                          }`}
                        >
                          {visibleList.map((item: any) => (
                            <article
                              className="bs-person-card"
                              key={`${activeTab}-${
                                item.connection_id ||
                                item.connected_user_id ||
                                item.user_id
                              }`}
                            >
                              {activeTab === "all" && (
                                <button
                                  className="bs-remove-suggestion"
                                  type="button"
                                  title="Remove suggestion"
                                  aria-label="Remove suggestion"
                                  onClick={() => handleRemoveSuggestion(item.user_id)}
                                >
                                  <i className="feather-x" />
                                </button>
                              )}

                              <div className="bs-person-cover" />

                              <div className="bs-person-body">
                                <UserAvatar item={item} size="lg" />

                                <h4>
                                  {item.user_fullname ||
                                    item.contact_name ||
                                    "Unknown User"}
                                </h4>

                                <p>{getCardDesignation(item)}</p>

                                <div className="bs-person-company">
                                  <ClientLogo item={item} />
                                  <span>{getCommunityText(item)}</span>
                                </div>

                                {Number(item.mutual_connections || 0) > 0 && (
                                  <div className="bs-person-meta single">
                                    <span>
                                      <i className="feather-users" />
                                      {item.mutual_connections} mutuals
                                    </span>
                                  </div>
                                )}

                                {renderMutualAvatars(item)}
                                {renderActionButton(item)}
                              </div>
                            </article>
                          ))}
                        </div>

                        {activeTab === "all" && (
                          <div className="bs-load-more-wrap">
                            <button
                              className="bs-load-more-btn"
                              onClick={handleLoadMore}
                              disabled={loading || !userId}
                            >
                              Load More
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="bs-empty-card">
                        <div className="bs-empty-icon">
                          <i className="feather-users" />
                        </div>

                        <h4>
                          {activeTab === "pending"
                            ? "No Pending Requests"
                            : activeTab === "sent"
                            ? "No Sent Invitations"
                            : activeTab === "connections"
                            ? "No Connections Yet"
                            : "No Users Found"}
                        </h4>

                        <p>
                          {activeTab === "all"
                            ? "Try searching with another keyword."
                            : "Nothing to show here right now."}
                        </p>
                      </div>
                    )}
                  </>
                )}
                  </>
                )}
              </main>
            </div>
          </div>
        </div>
      </div>

      {removeModalOpen && (
        <div className="bs-confirm-backdrop" onClick={closeRemoveConnectionModal}>
          <div
            className="bs-confirm-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="bs-confirm-icon">
              <i className="feather-user-x" />
            </div>

            <h3>Remove connection?</h3>

            <p>
              Are you sure you want to remove{" "}
              <strong>
                {selectedRemoveItem?.user_fullname ||
                  selectedRemoveItem?.contact_name ||
                  "this member"}
              </strong>{" "}
              from your connections?
            </p>

            <div className="bs-confirm-actions">
              <button
                type="button"
                className="bs-confirm-cancel"
                onClick={closeRemoveConnectionModal}
                disabled={actionLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="bs-confirm-remove"
                onClick={confirmRemoveConnection}
                disabled={actionLoading}
              >
                {actionLoading ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .bs-users-main {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: 315px;
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .bs-middle-bottom,
        .bs-users-content {
          width: 100%;
          max-width: 100%;
        }

        .bs-network-layout {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        .bs-network-main {
          width: 100%;
          min-width: 0;
        }

        .bs-overview-top-card,
        .bs-pending-strip,
        .bs-section-head,
        .bs-person-card,
        .bs-loader-card,
        .bs-empty-card,
        .bs-profile-hero-card,
        .bs-profile-section-card,
        .bs-profile-side-card,
        .bs-profile-page-card {
          background: #ffffff;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
        }

        /* ==========================
           GUEST NETWORK STATE
        ========================== */

        .bs-guest-network-card {
          width: 100%;
          min-height: 260px;
          padding: 34px 24px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          box-shadow:
            0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .bs-guest-network-icon {
          width: 52px;
          height: 52px;
          margin-bottom: 14px;
          border: 1px solid #dce7f7;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 22px;
        }

        .bs-guest-network-card h2 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .bs-guest-network-lead {
          margin: 8px 0 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.55;
        }

        .bs-guest-network-note {
          max-width: 420px;
          margin: 6px 0 0;
          color: #8a93a3;
          font-size: 12px;
          line-height: 1.55;
        }

        /* ==========================
           OVERVIEW HEADER
        ========================== */

        .bs-overview-top-card {
          padding: 14px 16px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 18px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          border-color: #e2eaf6;
          box-shadow: 0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .bs-overview-title-block h2 {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .bs-overview-title-block p {
          margin: 0;
          color: #8a93a3;
          font-size: 13px;
          line-height: 1.5;
          font-weight: 400;
        }

        .bs-overview-stats {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-shrink: 0;
        }

        .bs-overview-stat {
          min-height: 26px;
          padding: 0 9px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          display: inline-flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 4px;
          white-space: nowrap;
          transition: 0.2s ease;
        }

        .bs-overview-stat:hover,
        .bs-overview-stat.active {
          background: #eef4ff;
          border-color: #c9d9f8;
        }

        .bs-overview-stat strong,
        .bs-overview-stat span {
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
          margin: 0;
        }

        /* ==========================
           REQUEST STRIP
        ========================== */

        .bs-pending-strip {
          min-height: 44px;
          padding: 9px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.035);
        }

        .bs-pending-strip strong {
          color: #475569;
          font-size: 12px;
          font-weight: 700;
        }

        .bs-pending-strip button {
          min-height: 28px;
          padding: 0 10px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: #f5f8ff;
          color: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }

        /* ==========================
           SECTION HEADER
        ========================== */

        .bs-section-head {
          padding: 14px 16px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 16px;
        }

        .bs-section-head h3 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.3;
        }

        .bs-section-head p {
          margin: 4px 0 0;
          color: #8a93a3;
          font-size: 12px;
          line-height: 1.5;
          font-weight: 400;
        }

        .bs-section-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          flex-wrap: wrap;
          flex: 1;
        }

        .bs-small-action {
          min-height: 30px;
          padding: 0 10px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: #f5f8ff;
          color: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
          white-space: nowrap;
          cursor: pointer;
        }

        .bs-small-action:hover {
          background: #edf3ff;
        }

        .bs-search-box {
          position: relative;
          width: 320px;
          max-width: 100%;
          height: 40px;
          display: flex;
          align-items: center;
        }

        .bs-search-box i {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 1;
          color: #9aa4b2;
          font-size: 14px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .bs-search-box input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          padding: 0 12px 0 36px;
          outline: none;
          color: #1f2937;
          background: rgba(255, 255, 255, 0.92);
          font-size: 13px;
          box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.05);
        }

        .bs-search-box input::placeholder {
          color: #9aa4b2;
          font-weight: 400;
        }

        .bs-search-box input:focus {
          border-color: #2F6FE4;
          box-shadow: 0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .bs-search-box input:disabled {
          cursor: not-allowed;
          opacity: 0.6;
          background: #f8fafc;
        }

        /* ==========================
           PEOPLE GRID
        ========================== */

        .bs-people-grid,
        .bs-people-list-mode {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          width: 100%;
        }

        .bs-person-card {
          position: relative;
          min-width: 0;
          overflow: hidden;
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .bs-person-card:hover {
          transform: translateY(-2px);
          border-color: #c9d9f8;
          box-shadow: 0 12px 28px rgba(10, 35, 84, 0.10);
        }

        .bs-person-cover {
          height: 58px;
          background: linear-gradient(135deg, #eaf2ff 0%, #f4f7fc 100%);
        }

        .bs-remove-suggestion {
          position: absolute;
          right: 9px;
          top: 9px;
          z-index: 3;
          width: 24px;
          height: 24px;
          border: 0;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          background: rgba(15, 23, 42, 0.68);
          font-size: 12px;
          cursor: pointer;
        }

        .bs-person-body {
          padding: 0 14px 14px;
          text-align: center;
        }

        .bs-avatar {
          position: relative;
          overflow: hidden;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          border: 3px solid #ffffff;
          box-shadow: 0 5px 14px rgba(15, 23, 42, 0.10);
        }

        .bs-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .bs-avatar-lg {
          width: 72px;
          height: 72px;
          margin-top: -36px;
        }

        .bs-avatar-md {
          width: 50px;
          height: 50px;
        }

        .bs-avatar-sm {
          width: 34px;
          height: 34px;
        }

        .bs-avatar-fallback {
          width: 100%;
          height: 100%;
          display: none;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 22px;
          font-weight: 700;
        }

        .bs-avatar-sm .bs-avatar-fallback {
          font-size: 13px;
        }

        .bs-avatar-fallback.show {
          display: flex;
        }

        .bs-person-body h4 {
          margin: 7px 0 0;
          overflow: hidden;
          color: #0f1b34;
          font-family: 'Fraunces', serif;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.3;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-person-body > p {
          margin: 3px 0 0;
          color: #64748b;
          font-size: 11.5px;
          line-height: 1.35;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bs-person-company {
          width: fit-content;
          max-width: 100%;
          min-height: 24px;
          margin: 8px auto 0;
          padding: 4px 8px;
          border: 1px solid #edf1f6;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          color: #64748b;
          background: #f8fafc;
          font-size: 10.5px;
          font-weight: 600;
        }

        .bs-person-company span {
          max-width: 165px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-client-logo,
        .bs-client-logo-fallback {
          width: 16px;
          height: 16px;
          min-width: 16px;
          border: 1px solid #dce7f7;
          border-radius: 5px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 9px;
        }

        .bs-client-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .bs-client-logo .bs-client-logo-fallback {
          display: none;
          width: 100%;
          height: 100%;
          border: 0;
          border-radius: 0;
        }

        .bs-client-logo .bs-client-logo-fallback.show {
          display: inline-flex;
        }

        .bs-person-meta {
          margin-top: 7px;
        }

        .bs-person-meta span {
          min-height: 22px;
          width: fit-content;
          margin: 0 auto;
          padding: 0 8px;
          border: 1px solid #edf1f6;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          color: #8a93a3;
          background: #f8fafc;
          font-size: 10px;
          white-space: nowrap;
        }

        .bs-person-meta i {
          color: #2F6FE4;
        }

        .bs-mutual-box {
          min-height: 28px;
          margin-top: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
        }

        .bs-mutual-stack {
          display: flex;
          padding-left: 8px;
        }

        .bs-mutual-avatar {
          width: 22px;
          height: 22px;
          margin-left: -8px;
          overflow: hidden;
          border: 2px solid #ffffff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 9px;
          font-weight: 700;
        }

        .bs-mutual-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .bs-mutual-box p {
          margin: 0;
          max-width: 150px;
          overflow: hidden;
          color: #64748b;
          font-size: 10px;
          line-height: 1.3;
          text-align: left;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ==========================
           BUTTONS
        ========================== */

        .bs-connect-btn,
        .bs-btn {
          min-height: 32px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          transition: 0.2s ease;
        }

        .bs-person-card .bs-connect-btn {
          width: auto;
          min-width: 112px;
          padding: 0 14px;
          margin: 10px auto 0;
          border: 1px solid #2F6FE4;
          color: #2F6FE4;
          background: #ffffff;
        }

        .bs-person-card .bs-connect-btn:hover {
          color: #ffffff;
          background: #2F6FE4;
        }

        .bs-connect-btn.sent {
          border-color: #fed7aa;
          background: #fff7ed;
          color: #c2410c;
        }

        .bs-card-actions.two,
        .bs-card-actions.three {
          display: grid;
          gap: 6px;
          margin-top: 10px;
        }

        .bs-card-actions.two {
          grid-template-columns: 1fr 1fr;
        }

        .bs-card-actions.three {
          grid-template-columns: repeat(3, 1fr);
        }

        .bs-card-actions .bs-btn {
          width: 100%;
          margin: 0;
          padding: 0 7px;
        }

        .bs-btn-primary {
          border: 1px solid #2F6FE4;
          background: #2F6FE4;
          color: #ffffff;
        }

        .bs-btn-primary:hover {
          background: #1f57c7;
        }

        .bs-btn-light,
        .bs-btn-outline {
          border: 1px solid #dce7f7;
          background: #f5f8ff;
          color: #2F6FE4;
        }

        .bs-btn-connected {
          border: 1px solid #d7eadf;
          background: #f2fbf6;
          color: #198754;
        }

        .bs-btn-sent {
          border: 1px solid #fed7aa;
          background: #fff7ed;
          color: #c2410c;
        }

        .bs-btn-danger {
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #dc2626;
        }

        .bs-btn-danger:hover {
          background: #ffe4e6;
        }

        .bs-connect-btn:disabled,
        .bs-btn:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        /* ==========================
           EMPTY / LOADING
        ========================== */

        .bs-loader-card,
        .bs-empty-card {
          min-height: 200px;
          padding: 28px 22px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border-style: dashed;
          box-shadow: none;
        }

        .bs-loader-card p,
        .bs-empty-card p {
          margin: 6px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.55;
        }

        .bs-empty-icon {
          width: 44px;
          height: 44px;
          margin-bottom: 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          font-size: 20px;
        }

        .bs-empty-card h4 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .bs-load-more-wrap {
          display: flex;
          justify-content: center;
          margin-top: 16px;
        }

        .bs-load-more-btn {
          min-width: 130px;
          min-height: 34px;
          padding: 0 14px;
          border: 1px solid #dce7f7;
          border-radius: 10px;
          background: #ffffff;
          color: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }

        /* ==========================
           PROFILE DETAIL
        ========================== */

        .bs-profile-page-wrap {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .bs-back-btn {
          width: fit-content;
          min-height: 32px;
          padding: 0 11px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #ffffff;
          color: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
        }

        .bs-back-btn:hover {
          background: #f5f8ff;
        }

        .bs-profile-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 280px;
          gap: 12px;
          align-items: start;
        }

        .bs-profile-main-column,
        .bs-profile-side-column {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 0;
        }

        .bs-profile-cover-image {
          position: relative;
          height: 118px;
          padding: 14px;
          display: flex;
          justify-content: flex-end;
          background: linear-gradient(135deg, #eaf2ff, #f3f7ff);
          background-size: cover;
          background-position: center;
        }

        .bs-profile-cover-image span {
          position: relative;
          z-index: 2;
          height: fit-content;
          padding: 5px 9px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          color: #5f7fae;
          background: rgba(255,255,255,.82);
          font-size: 10.5px;
          font-weight: 700;
        }

        .bs-profile-hero-body {
          padding: 0 16px 16px;
        }

        .bs-profile-avatar-large {
          width: fit-content;
          margin-top: -36px;
        }

        .bs-profile-avatar-large .bs-avatar-lg {
          width: 76px;
          height: 76px;
          margin-top: 0;
          border-width: 4px;
        }

        .bs-profile-title-row {
          margin-top: 8px;
        }

        .bs-profile-name-line {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .bs-profile-title-row h2 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 22px;
          font-weight: 700;
          line-height: 1.2;
        }

        .bs-profile-title-row p {
          margin: 4px 0 0;
          color: #475569;
          font-size: 12.5px;
          line-height: 1.4;
        }

        .bs-profile-location {
          display: block;
          margin-top: 5px;
          color: #94a3b8;
          font-size: 11.5px;
        }

        .bs-profile-status-row {
          margin-top: 10px;
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .bs-status-pill {
          min-height: 25px;
          padding: 4px 9px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 10.5px;
          font-weight: 700;
        }

        .bs-status-pill.success {
          background: #f2fbf6;
          border: 1px solid #d7eadf;
          color: #198754;
        }

        .bs-status-pill.neutral {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #64748b;
        }

        .bs-profile-section-card,
        .bs-profile-side-card {
          padding: 14px;
        }

        .bs-profile-section-card h3,
        .bs-profile-side-card h3 {
          margin: 0 0 10px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 16px;
          font-weight: 700;
        }

        .bs-highlight-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .bs-highlight-item,
        .bs-side-info-list > div,
        .bs-community-profile-box {
          border: 1px solid #edf1f6;
          border-radius: 10px;
          background: #f8fafc;
        }

        .bs-highlight-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px;
        }

        .bs-highlight-item > i,
        .bs-experience-icon {
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
        }

        .bs-highlight-item strong {
          display: block;
          color: #0f1b34;
          font-size: 12px;
          font-weight: 700;
        }

        .bs-highlight-item p,
        .bs-about-text,
        .bs-experience-row p,
        .bs-experience-row span,
        .bs-experience-row small {
          margin: 3px 0 0;
          color: #64748b;
          font-size: 11.5px;
          line-height: 1.45;
        }

        .bs-about-text {
          margin: 0;
        }

        .bs-experience-row {
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }

        .bs-experience-row h4 {
          margin: 0;
          color: #0f1b34;
          font-size: 13px;
          font-weight: 700;
        }

        .bs-side-info-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .bs-side-info-list > div {
          padding: 9px 10px;
        }

        .bs-side-info-list span {
          display: block;
          margin-bottom: 3px;
          color: #94a3b8;
          font-size: 9.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .2px;
        }

        .bs-side-info-list strong {
          display: block;
          color: #0f1b34;
          font-size: 11.5px;
          font-weight: 600;
          overflow-wrap: anywhere;
        }

        .bs-community-profile-box {
          margin-bottom: 10px;
          padding: 10px;
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .bs-community-profile-box strong {
          display: block;
          color: #0f1b34;
          font-size: 12px;
          font-weight: 700;
        }

        .bs-community-profile-box span {
          display: block;
          margin-top: 2px;
          color: #64748b;
          font-size: 10.5px;
        }

        .bs-profile-loading-card,
        .bs-profile-empty-state {
          min-height: 260px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        /* ==========================
           CONFIRM MODAL
        ========================== */

        .bs-confirm-backdrop {
          position: fixed;
          inset: 0;
          z-index: 99999;
          padding: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(15, 23, 42, 0.42);
        }

        .bs-confirm-modal {
          width: 100%;
          max-width: 380px;
          padding: 22px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          text-align: center;
          background: #ffffff;
          box-shadow: 0 22px 60px rgba(15, 23, 42, 0.20);
        }

        .bs-confirm-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 12px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #dc2626;
          background: #fff1f2;
          font-size: 20px;
        }

        .bs-confirm-modal h3 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
        }

        .bs-confirm-modal p {
          margin: 8px 0 0;
          color: #64748b;
          font-size: 12.5px;
          line-height: 1.5;
        }

        .bs-confirm-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: 18px;
        }

        .bs-confirm-cancel,
        .bs-confirm-remove {
          min-height: 34px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
        }

        .bs-confirm-cancel {
          border: 1px solid #dce7f7;
          background: #f5f8ff;
          color: #2F6FE4;
        }

        .bs-confirm-remove {
          border: 1px solid #dc2626;
          background: #dc2626;
          color: #ffffff;
        }

        /* ==========================
           RESPONSIVE
        ========================== */

        @media (max-width: 1400px) {
          .bs-users-main {
            padding-right: 290px;
          }
        }

        @media (max-width: 1200px) {
          .bs-users-main {
            padding-left: 20px;
            padding-right: 20px;
          }

          .bs-network-layout {
            margin-left: 0;
            max-width: 100%;
          }

          .bs-people-grid,
          .bs-people-list-mode {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 991px) {
          .bs-section-head {
            flex-direction: column;
          }

          .bs-section-actions {
            justify-content: flex-start;
            width: 100%;
          }

          .bs-search-box {
            width: 100%;
          }

          .bs-profile-layout {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .bs-users-main {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .bs-middle-bottom,
          .bs-users-content {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .bs-network-layout {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .bs-guest-network-card {
            min-height: 220px;
            padding: 28px 18px;
            border-radius: 14px;
          }

          .bs-guest-network-card h2 {
            font-size: 21px;
          }

          .bs-overview-top-card {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .bs-overview-title-block h2 {
            font-size: 21px;
          }

          .bs-overview-stats {
            gap: 5px;
          }

          .bs-section-head {
            padding: 13px;
            border-radius: 14px;
          }

          .bs-people-grid,
          .bs-people-list-mode {
            grid-template-columns: 1fr;
          }

          .bs-highlight-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .bs-overview-top-card {
            flex-direction: column;
          }

          .bs-overview-stats {
            width: 100%;
          }

          .bs-overview-stat {
            flex: 1;
          }

          .bs-card-actions.three {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default Users;